import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    Grid,
    MenuItem,
    Alert,
    Chip,
    Card,
    CardContent,
    Divider,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
    Tooltip,
} from '@mui/material';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import { TokenContext } from '../Context/TokenContext';
import { QUOTE_INTERVALS, createQuote } from '../Utils/quotesApi';
import { getOptions, getPricingPlans, getPermissions } from '../Utils/pricingApi';
import { formatEUR } from '../Utils/pdfExport';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const TYPE_MAP = { individual: 'Particulier', company: 'Entreprise' };

// Même transformation que QuotesPage.js/ClientsPage.js/ProspectsPage.js — voir
// leur commentaire pour le détail du modèle client_management partagé.
function mapPerson(c) {
    const primaryUser = c.Utilisateurs?.[0];
    return {
        id: c.id_client,
        // Identifiant OAuth du compte — c'est LUI qui identifie le
        // destinataire pour Medusa (accès au devis, licence), pas id_client
        // qui n'est qu'un autoincrement interne à client_management.
        publicId: primaryUser?.public_id || null,
        name: c.type_client === 'company' ? (c.raison_sociale || c.nom || '—') : (c.nom || c.raison_sociale || '—'),
        contact: primaryUser ? `${primaryUser.prenom || ''} ${primaryUser.nom || ''}`.trim() || '—' : '—',
        email: primaryUser?.email || '—',
        phone: primaryUser?.phone || '—',
        type: TYPE_MAP[c.type_client] || c.type_client || '—',
    };
}

let itemKeySeq = 0;
function emptyItem() {
    return {
        key: `item-${++itemKeySeq}`,
        contentMode: 'libre', // 'catalogue' | 'libre'
        application: null,
        options: [], // application_option_ids sélectionnées
        plan: null,
        authorizations: [], // clés de permission sélectionnées
        optionsList: [],
        plansList: [],
        permissionsList: [],
        title: '',
        description: '',
        amount: '',
    };
}

const emptyForm = {
    recipientMode: 'client', // 'client' | 'prospect'
    person: null,
    title: '',
    description: '',
    type: 'one_time',
    interval: 'month',
    subscription_ends_at: '',
    expires_in_days: 14,
};

function PersonPicker({ label, options, value, onChange }) {
    return (
        <>
            <Autocomplete
                options={options}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(p) => p.name || ''}
                renderOption={(props, p) => (
                    <li {...props} key={p.id}>
                        <Box>
                            <Typography variant="body1">{p.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {p.contact !== '—' ? `${p.contact} — ` : ''}{p.email}
                            </Typography>
                        </Box>
                    </li>
                )}
                value={value}
                onChange={(e, v) => onChange(v)}
                renderInput={(params) => <TextField {...params} label={label} />}
                noOptionsText="Aucun résultat"
            />
            {value && (
                <Alert severity="success" icon={false} sx={{ mt: 1 }}>
                    <strong>{value.name}</strong>
                    {value.contact !== '—' ? ` (${value.contact})` : ''}
                    {' — '}{value.email}
                    {value.phone !== '—' ? ` — ${value.phone}` : ''}
                </Alert>
            )}
            {value && !value.publicId && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    Ce destinataire n'a pas de compte en ligne — le devis ne sera pas
                    restreint à un compte précis (n'importe quel utilisateur connecté
                    pourra ouvrir le lien).
                </Alert>
            )}
        </>
    );
}

export default function CreateQuotePage() {
    const { accessToken } = useContext(TokenContext);
    const navigate = useNavigate();

    const [clients, setClients] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [applications, setApplications] = useState([]);

    const [form, setForm] = useState(emptyForm);
    const [items, setItems] = useState([emptyItem()]);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [createdLink, setCreatedLink] = useState(null); // { ephemeral_url }
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!accessToken) return;
        const headers = { Authorization: `Bearer ${accessToken}` };
        axios.get(`${API_URL}/api/clients`, { headers })
            .then((res) => setClients((res.data || []).map(mapPerson)))
            .catch(() => {});
        axios.get(`${API_URL}/api/prospects`, { headers })
            .then((res) => setProspects(
                (res.data || [])
                    .filter((p) => !['mort', 'converti'].includes(p.statut_prospect))
                    .map(mapPerson)
            ))
            .catch(() => {});
        axios.get(`${API_URL}/api/applications`, { headers })
            .then((res) => setApplications(res.data || []))
            .catch(() => {});
    }, [accessToken]);

    const total = useMemo(
        () => items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0),
        [items]
    );

    const updateItem = (key, patch) => {
        setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
    };

    const addItem = () => setItems((prev) => [...prev, emptyItem()]);
    const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

    const onApplicationChange = (key, application) => {
        updateItem(key, {
            application,
            options: [],
            plan: null,
            authorizations: [],
            optionsList: [],
            plansList: [],
            permissionsList: [],
            title: items.find((it) => it.key === key)?.title || application?.name || '',
        });
        if (!application) return;
        getOptions(accessToken, application.client_id)
            .then((options) => updateItem(key, { optionsList: options || [] }))
            .catch(() => {});
        getPricingPlans(accessToken, 'application', application.client_id)
            .then((plans) => updateItem(key, { plansList: plans || [] }))
            .catch(() => {});
        getPermissions(accessToken, application.client_id)
            .then((permissions) => updateItem(key, { permissionsList: permissions || [] }))
            .catch(() => {});
    };

    const onOptionsChange = (key, options) => {
        updateItem(key, { options });
    };

    const onPlanChange = (key, plan) => {
        const item = items.find((it) => it.key === key);
        updateItem(key, {
            plan,
            amount: plan?.base_amount != null ? String(plan.base_amount) : item?.amount,
            title: item?.title || plan?.name || '',
            authorizations: plan?.authorizations?.length ? plan.authorizations : item?.authorizations,
        });
    };

    const onAuthorizationsChange = (key, permissions) => {
        updateItem(key, { authorizations: permissions.map((p) => p.key) });
    };

    const handleSave = () => {
        if (!form.person) {
            setFormError(form.recipientMode === 'client' ? 'Sélectionnez un client' : 'Sélectionnez un prospect');
            return;
        }
        if (!form.title.trim()) {
            setFormError('Le titre du devis est requis');
            return;
        }
        if (items.length === 0) {
            setFormError('Ajoutez au moins une ligne');
            return;
        }
        for (const [index, item] of items.entries()) {
            const amountValue = Number(item.amount);
            if (!Number.isFinite(amountValue) || amountValue <= 0) {
                setFormError(`Ligne ${index + 1} : le montant doit être un nombre positif`);
                return;
            }
        }
        if (form.type === 'subscription' && form.subscription_ends_at) {
            if (new Date(form.subscription_ends_at).getTime() <= Date.now()) {
                setFormError("La date de fin de l'abonnement doit être dans le futur");
                return;
            }
        }

        const payload = {
            id_client: form.person.id,
            // Le compte du destinataire pour Medusa (accès au devis côté
            // boutique, licence à activer) — id_client n'est qu'une
            // référence de confort côté admin, jamais une identité.
            customer_public_id: form.person.publicId || undefined,
            recipient_name: form.person.contact !== '—' ? form.person.contact : form.person.name,
            recipient_email: form.person.email !== '—' ? form.person.email : undefined,
            recipient_phone: form.person.phone !== '—' ? form.person.phone : undefined,
            title: form.title.trim(),
            description: form.description.trim() || undefined,
            currency: 'eur',
            type: form.type,
            interval: form.type === 'subscription' ? form.interval : undefined,
            subscription_ends_at: form.type === 'subscription' && form.subscription_ends_at
                ? new Date(form.subscription_ends_at).toISOString()
                : undefined,
            expires_in_days: Number(form.expires_in_days) || 14,
            items: items.map((item) => ({
                title: item.title.trim() || undefined,
                description: item.description.trim() || undefined,
                amount: Number(item.amount),
                application_client_id: item.contentMode === 'catalogue' ? item.application?.client_id : undefined,
                application_option_ids: item.contentMode === 'catalogue' && item.options.length
                    ? item.options.map((o) => String(o.id))
                    : undefined,
                authorizations: item.contentMode === 'catalogue' && item.authorizations.length
                    ? item.authorizations
                    : undefined,
                pricing_plan_id: item.contentMode === 'catalogue' ? item.plan?.id : undefined,
            })),
        };

        setSaving(true);
        setFormError('');
        createQuote(accessToken, payload)
            .then((data) => setCreatedLink({ ephemeral_url: data.ephemeral_url }))
            .catch((err) => setFormError(err.response?.data?.message || "Erreur lors de la création du devis"))
            .finally(() => setSaving(false));
    };

    const handleCopy = () => {
        if (!createdLink) return;
        navigator.clipboard.writeText(createdLink.ephemeral_url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleBack = () => navigate('/quotes');

    if (createdLink) {
        return (
            <Box sx={{ p: 3, maxWidth: 640 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <RequestQuoteOutlinedIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        Lien généré
                    </Typography>
                </Box>
                <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                            <Alert severity="success">
                                Copiez ce lien maintenant — il ne sera plus jamais affiché.
                            </Alert>
                            <TextField
                                fullWidth
                                value={createdLink.ephemeral_url}
                                InputProps={{ readOnly: true }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleCopy}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                            >
                                {copied ? 'Copié !' : 'Copier le lien'}
                            </Button>
                            <Button onClick={handleBack}>Retour aux devis</Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <RequestQuoteOutlinedIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    Nouveau devis
                </Typography>
            </Box>

            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Destinataire</Typography>
                            <ToggleButtonGroup
                                exclusive
                                value={form.recipientMode}
                                onChange={(e, v) => v && setForm((f) => ({ ...f, recipientMode: v, person: null }))}
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="client">Client existant</ToggleButton>
                                <ToggleButton value="prospect">Prospect existant</ToggleButton>
                            </ToggleButtonGroup>
                            {form.recipientMode === 'client' ? (
                                <PersonPicker
                                    label="Rechercher un client (nom, entreprise, contact)"
                                    options={clients}
                                    value={form.person}
                                    onChange={(v) => setForm((f) => ({ ...f, person: v }))}
                                />
                            ) : (
                                <PersonPicker
                                    label="Rechercher un prospect (nom, entreprise, contact)"
                                    options={prospects}
                                    value={form.person}
                                    onChange={(v) => setForm((f) => ({ ...f, person: v }))}
                                />
                            )}
                            {form.recipientMode === 'prospect' && prospects.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Aucun prospect exploitable — créez-le d'abord depuis l'écran "Prospects".
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Informations générales du devis</Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Titre (visible par le client)" required
                                        value={form.title}
                                        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline rows={2}
                                        label="Note (optionnel, visible par le client)"
                                        value={form.description}
                                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth select label="Type"
                                        value={form.type}
                                        onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                                        <MenuItem value="one_time">Paiement unique</MenuItem>
                                        <MenuItem value="subscription">Abonnement récurrent</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth type="number" label="Validité du lien (jours)"
                                        value={form.expires_in_days}
                                        onChange={(e) => setForm((f) => ({ ...f, expires_in_days: e.target.value }))} />
                                </Grid>
                                {form.type === 'subscription' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth select label="Fréquence"
                                                value={form.interval}
                                                onChange={(e) => setForm((f) => ({ ...f, interval: e.target.value }))}>
                                                {QUOTE_INTERVALS.map((i) => (
                                                    <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth type="date" label="Se termine le (optionnel)"
                                                InputLabelProps={{ shrink: true }}
                                                value={form.subscription_ends_at}
                                                onChange={(e) => setForm((f) => ({ ...f, subscription_ends_at: e.target.value }))} />
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Ex : 50 € / mois pendant 2 ans — laissez vide pour un abonnement sans
                                                fin prévue.
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Le type et la fréquence s'appliquent à l'ensemble des lignes ci-dessous — Stripe ne
                                permet pas de mélanger paiement unique et abonnement dans un même devis.
                            </Typography>
                        </CardContent>
                    </Card>

                    <Typography variant="h6" sx={{ mb: 2 }}>Lignes du devis (licences)</Typography>
                    {items.map((item, index) => (
                        <Card key={item.key} sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>Ligne {index + 1}</Typography>
                                    <Tooltip title="Supprimer cette ligne">
                                        <span>
                                            <IconButton
                                                color="error"
                                                disabled={items.length === 1}
                                                onClick={() => removeItem(item.key)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>

                                <ToggleButtonGroup
                                    exclusive
                                    value={item.contentMode}
                                    onChange={(e, v) => v && updateItem(item.key, {
                                        contentMode: v,
                                        application: null,
                                        options: [],
                                        plan: null,
                                        authorizations: [],
                                        optionsList: [],
                                        plansList: [],
                                        permissionsList: [],
                                    })}
                                    sx={{ mb: 3 }}
                                >
                                    <ToggleButton value="libre">Libre</ToggleButton>
                                    <ToggleButton value="catalogue">Application du catalogue</ToggleButton>
                                </ToggleButtonGroup>

                                <Grid container spacing={3}>
                                    {item.contentMode === 'catalogue' && (
                                        <>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={applications}
                                                    getOptionLabel={(a) => a.name || a.client_id}
                                                    value={item.application}
                                                    onChange={(e, v) => onApplicationChange(item.key, v)}
                                                    renderInput={(params) => <TextField {...params} label="Application" />}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    disabled={!item.application}
                                                    options={item.optionsList}
                                                    getOptionLabel={(o) => o.name || o.key}
                                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                                    value={item.options}
                                                    onChange={(e, v) => onOptionsChange(item.key, v)}
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, i) => (
                                                            <Chip label={option.name || option.key} {...getTagProps({ index: i })} key={option.id} />
                                                        ))
                                                    }
                                                    renderInput={(params) => <TextField {...params} label="Options (optionnel, plusieurs possibles)" />}
                                                    noOptionsText="Aucune option pour cette application"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    disabled={!item.application}
                                                    options={item.plansList}
                                                    getOptionLabel={(p) => `${p.name} — ${formatEUR(Number(p.base_amount) || 0)}`}
                                                    value={item.plan}
                                                    onChange={(e, v) => onPlanChange(item.key, v)}
                                                    renderInput={(params) => <TextField {...params} label="Tarif catalogue (optionnel)" />}
                                                    noOptionsText="Aucun tarif enregistré pour cette application"
                                                />
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Reprend le prix et les permissions du tarif choisi — les deux
                                                    restent modifiables ci-dessous pour personnaliser cette ligne.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    disabled={!item.application}
                                                    options={item.permissionsList}
                                                    getOptionLabel={(p) => p.name || p.key}
                                                    isOptionEqualToValue={(a, b) => a.key === b.key}
                                                    value={item.permissionsList.filter((p) => item.authorizations.includes(p.key))}
                                                    onChange={(e, v) => onAuthorizationsChange(item.key, v)}
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((permission, i) => (
                                                            <Chip label={permission.name || permission.key} {...getTagProps({ index: i })} key={permission.key} />
                                                        ))
                                                    }
                                                    renderInput={(params) => <TextField {...params} label="Permissions accordées" />}
                                                    noOptionsText="Aucune permission définie pour cette application"
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Titre de la ligne" required
                                            value={item.title}
                                            onChange={(e) => updateItem(item.key, { title: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="number" label="Montant (€)" required
                                            value={item.amount}
                                            onChange={(e) => updateItem(item.key, { amount: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth multiline rows={2}
                                            label="Description (optionnel)"
                                            value={item.description}
                                            onChange={(e) => updateItem(item.key, { description: e.target.value })} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}

                    <Button startIcon={<AddIcon />} onClick={addItem} size="large" sx={{ color: 'primary.main' }}>
                        Ajouter une ligne
                    </Button>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3, position: 'sticky', top: 16 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Récapitulatif</Typography>
                            <Stack spacing={1} sx={{ mb: 2 }}>
                                {items.map((item, index) => (
                                    <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '65%' }}>
                                            {item.title || `Ligne ${index + 1}`}
                                        </Typography>
                                        <Typography variant="body2">{formatEUR(Number(item.amount) || 0)}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600}>Total</Typography>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {formatEUR(total)}
                                    {form.type === 'subscription' ? ` / ${form.interval}` : ''}
                                </Typography>
                            </Box>
                            {form.type === 'subscription' && form.subscription_ends_at && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Se termine le {new Date(form.subscription_ends_at).toLocaleDateString('fr-FR')}
                                </Typography>
                            )}
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' }, mb: 2 }}
                            >
                                {saving ? 'Création...' : 'Créer le devis'}
                            </Button>
                            <Button fullWidth onClick={handleBack}>Annuler</Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
